import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SubsectionService } from '../../../shared/services/subsection.service';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  BaseTreeNode,
  isShareLinkAssetNode,
  isSubsectionFileNode,
  TreeNodeType,
  TreeState,
} from '../../../project-subsection/models/tree-node';
import { AuthService } from '../../../shared/services/auth.service';
import { TreeManagerService, TreeManagerTreeType } from '../../../project-subsection/services/tree-manager.service';
import {
  ActiveSubsectionState,
  OptionVersionSimple,
  ProjectSubsection,
  ShareLinkAssetEntityTypes,
  SubsectionNavigationParams,
} from '../../../shared/interfaces';
import { ProjectService } from '../../../shared/services/project.service';
import { TopMediaInfoService } from './top-media-info.service';
import { ReleaseTreeService } from '../../../project-subsection/services/release-tree-service';
import { PermissionService } from '../../../shared/services/permissions.service';
import { ShareLinkTreeService } from '../../../project-subsection/services/share-link-tree.service';
import { shareAsset } from '../../../project-subsection/utilities/share-link-utilities';
import { OverlayService } from '../../../shared/services/overlayService';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

import { ReleaseTreeState, ShareLinkAssetVM } from '../../../project-subsection/models';
import { CommentPanelWidthService } from '../../services/comment-panel-width.service';
import { AppStateService } from '../../services/app-state.service';
import { ActivatedRoute } from '@angular/router';
@Component({
  selector: 'top-media-info',
  templateUrl: './top-media-info.component.html',
  styleUrl: './top-media-info.component.scss',
  standalone: false,
})
export class TopMediaInfoComponent implements OnInit, OnDestroy {
  private readonly treeManagerService: TreeManagerService = inject(TreeManagerService);
  private readonly authService: AuthService = inject(AuthService);
  @Input() isBusy: boolean = false;
  @Input() subSectionState: ActiveSubsectionState = null;
  @Input() hasEditPermission: boolean = false;
  @Input() assetInfoVisible: boolean = true;
  @Input() assetListVisible: boolean = true;
  @Input() versionListVisible: boolean = true;
  @Input() shareable: boolean = true;
  @Input() showFullTopSubSectionNav: boolean = false;
  @Input() subSectionPreview: any;
  @Input() compareVisible: boolean = true;
  @Input() isKpfPreview: boolean = false;
  @Input() publicReviewLink: boolean = false;
  @Input() showNavigationButtons: boolean = true;
  @Input() isShareLinkPreview: boolean = false;
  @Input() isReleasePreview: boolean = false;
  @Input() votingVisible: boolean = true;
  @Input() commentVisible: boolean = true;
  @Input() isNewShareLink: boolean = false;
  @Input() isDashboard: boolean = false;
  @Input() isWidgetCollapsed: boolean = false;
  @Input() showListViewToggleButton: boolean = false;
  @Output() switchVersionClick: EventEmitter<number> = new EventEmitter<number>();
  @Output() manageVersionsClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() newVersionClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() downloadAssetClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() assetListClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() assetInfoClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() shareClick: EventEmitter<void> = new EventEmitter<void>();

  private readonly releaseTreeService: ReleaseTreeService = inject(ReleaseTreeService);
  private readonly subsectionService: SubsectionService = inject(SubsectionService);
  private readonly shareLinkService: ShareLinkTreeService = inject(ShareLinkTreeService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly topMediaInfoService: TopMediaInfoService = inject(TopMediaInfoService);
  private readonly permissionService: PermissionService = inject(PermissionService);
  private readonly overlayService: OverlayService = inject(OverlayService);
  private readonly translatePipe: TranslatePipe = inject(TranslatePipe);
  private readonly appState = inject(AppStateService);
  private readonly commentPanelWidthService = inject(CommentPanelWidthService);
  private releaseRecord: Record<string, any> = {};

  treeNodeType: typeof TreeNodeType = TreeNodeType;
  treeType: TreeManagerTreeType = TreeManagerTreeType.Subsection;
  readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  isShareLinksEnabled: boolean = false;
  cacheBuster = Date.now().toString();
  private readonly activatedRoute = inject(ActivatedRoute);
  subsectionState: ActiveSubsectionState;
  publicPage: boolean = false;
  get isFullscreen() {
    return this.topMediaInfoService.isFullscreen;
  }

  get isListViewMode(): boolean {
    return this.topMediaInfoService.isListViewMode;
  }

  get hasLockedVersionInCurrentOption(): boolean {
    if (!this.subSectionState?.activeOption?.simpleVersions || !this.subSectionState?.activeOption?.optionId) {
      return false;
    }

    // Check if any version in the current option is locked
    return this.subSectionState.activeOption.simpleVersions.some((version: OptionVersionSimple) => {
      const versionId: string = `${this.subSectionState?.activeOption?.optionId}-${version.versionNumber}`;
      return !!this.releaseRecord[versionId];
    });
  }

  get shouldDisableInteractiveElements(): boolean {
    return this.hasLockedVersionInCurrentOption || this.isReleasePreview;
  }

  get projectAvatarUrl(): string {
    return this.projectService?.project?.avatarUrl;
  }

  get projectName(): string {
    return this.projectService?.project?.name;
  }

  get showFullscreenButton(): boolean {
    return true; //!this.subSectionState?.reviewLink || this.isKpfPreview;
  }

  get treeState(): TreeState<BaseTreeNode> {
    return this.treeManagerService.getCurrentState();
  }

  get isMediaPreviewCommentsChatShowing(): boolean {
    return this.isCommentVisible && !this.isShareLinkPreview && !this.isDashboard && !this.isReleasePreview;
  }

  get isAssetShareable(): boolean {
    return this.shareable && this.treeType === TreeManagerTreeType.Subsection && this.hasEditPermission;
  }

  get shouldShowEditAssetButton(): boolean {
    return (
      !!this.subSectionPreview?.subSectionState?.reviewLink ||
      this.subSectionPreview?.versionData == null ||
      this.isShareLinkPreview ||
      this.isReleasePreview ||
      this.shouldDisableInteractiveElements
    );
  }

  ngOnInit(): void {
    this.publicPage = this.activatedRoute.snapshot.data.publicPage;
    this.treeManagerService
      .getActiveTreeType()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((treeType: TreeManagerTreeType) => {
        this.treeType = treeType;
      });

    // Subscribe to release tree service to get release record
    this.releaseTreeService
      .getState()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (state: ReleaseTreeState) => {
          this.releaseRecord = state?.releaseRecord || {};
        },
      });

    this.subsectionService.subsection$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((subsectionState: ActiveSubsectionState | null) => !!subsectionState?.activeFile)
      )
      .subscribe((subsectionState: ActiveSubsectionState) => {
        this.subsectionState = subsectionState;
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev.isShareLinkPreview === curr.isShareLinkPreview)
      )
      .subscribe({
        next: (state) => {
          this.isShareLinkPreview = state.isShareLinkPreview;
          if (this.isShareLinkPreview) {
            this.subsectionService.updateUIState({ commentsCollapsed: true });
          }
        },
      });

    if (this.topMediaInfoService.isFullscreen) {
      this.subsectionService.updateUIState({ explorerCollapsed: false });
      document.body.classList.toggle('media-preview-fullscreen');
      document.body.classList.toggle('dashboard-fullscreen');
      document.body.classList.toggle('share-link-fullscreen');
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  get isCommentVisible(): boolean {
    if (!this.commentVisible || !this.treeState?.selectedNode) {
      return false;
    }
    const selectedNode: BaseTreeNode = this.treeState?.selectedNode;
    return (
      isSubsectionFileNode(selectedNode) ||
      (isShareLinkAssetNode(selectedNode) && selectedNode.assetType === ShareLinkAssetEntityTypes.FILE)
    );
  }

  get canShowCommentIcon(): boolean {
    return this.appState.isEnableCommentPage() && !!this.appState.getActivePage();
  }

  get canShowTaskIcon(): boolean {
    return this.appState.isEnableTaskPage() && !!this.appState.getActivePage();
  }

  get isPlatformCommentsChatShowing(): boolean {
    return !this.publicReviewLink && !this.publicPage && !!this.authService.getAuthUser() && this.canShowCommentIcon;
  }

  get isPlatformTaskShowing(): boolean {
    return !this.publicPage && !!this.authService.getAuthUser() && this.canShowTaskIcon;
  }

  get isMediaPreviewTasksChatShowing(): boolean {
    const isTaskVisible = this.isCommentVisible;
    return isTaskVisible && !this.isShareLinkPreview && !this.isReleasePreview && !this.isDashboard;
  }

  get hasCommentPanelWidth(): boolean {
    return this.commentPanelWidthService.width() > 0;
  }

  get rightViewMode(): string {
    return this.subsectionService.uiState?.rightPanelView;
  }

  get isCommentExpanded(): boolean {
    const activePage = this.appState.getActivePage();
    return (
      (this.isPlatformCommentsChatShowing &&
        (activePage ? this.appState.isCommentPageCommentExpanded(activePage) : false)) ||
      (!this.subsectionService.uiState?.commentsCollapsed && this.isMediaPreviewCommentsChatShowing)
    );
  }

  switchVersion(versionNumber: number) {
    this.switchVersionClick.emit(versionNumber);
  }

  public showManageVersions(): void {
    this.manageVersionsClick.emit();
  }

  public createNewVersion(): void {
    this.newVersionClick.emit();
  }

  public downloadAsset(): void {
    this.downloadAssetClick.emit();
  }

  public toggleAssetList(): void {
    this.assetListClick.emit();
  }

  public toggleAssetInfo(): void {
    this.assetInfoClick.emit();
  }

  public toggleFullscreen(): void {
    document.body.classList.toggle('media-preview-fullscreen');
    document.body.classList.toggle('dashboard-fullscreen');
    if (this.isReleasePreview) {
      document.body.classList.toggle('release-fullscreen');
    }
    if (this.isShareLinkPreview) {
      document.body.classList.toggle('share-link-fullscreen');
    }
    this.topMediaInfoService.toggleFullscreen();
  }

  public navigateToPrevious(): void {
    this.treeManagerService.navigateToPrevious(true);
  }

  public navigateToNext(): void {
    this.treeManagerService.navigateToNext(true);
  }

  public navigateToNode(node: BaseTreeNode): void {
    this.treeManagerService.navigateToNode(node);
  }

  public goToHome(): void {
    this.treeManagerService.navigateToHome();
  }

  public toggleListView(): void {
    this.topMediaInfoService.updateState({
      isListViewMode: !this.topMediaInfoService.isListViewMode,
    });
  }

  openChat() {
    if (this.isMediaPreviewCommentsChatShowing) {
      this.subSectionPreview?.toggleComments();
    } else {
      const activePage = this.appState.getActivePage();
      const isExpanded = this.appState.isCommentPageCommentExpanded(activePage);
      this.appState.setCommentPageCommentExpanded(activePage, !isExpanded);
    }
  }

  openTask() {
    if (this.isMediaPreviewCommentsChatShowing) {
      const currentCollapsed = !!this.subsectionService.uiState?.commentsCollapsed;
      const currentRightPanelView = this.subsectionService.uiState?.rightPanelView;
      const finalCollapsed = currentRightPanelView === 'comment' ? false : !currentCollapsed;
      this.subsectionService.updateUIState({ commentsCollapsed: finalCollapsed, rightPanelView: 'task' });
    } else {
      const activePage = this.appState.getActivePage();
      const isExpanded = this.appState.isCommentPageCommentExpanded(activePage);

      const currentRightPanelView = this.subsectionService.uiState?.rightPanelView;
      const isExpandedVal = currentRightPanelView === 'comment' ? true : !isExpanded;

      this.subsectionService.updateUIState({ rightPanelView: 'task' });
      this.appState.setCommentPageCommentExpanded(activePage, isExpandedVal);
    }
  }
  navigateToPreviewPage() {
    const selectedSubsection: ProjectSubsection = this.subsectionState.subsection;
    const navigationParams: SubsectionNavigationParams = {
      projectId: this.subsectionState.activeFile.projectId,
      sectionId: this.subsectionState.activeFile.sectionId,
      subsectionId: selectedSubsection.id,
      topicId: this.subsectionState.activeOption.topicId,
      optionId: this.subsectionState.activeOption.optionId,
      versionNumber: this.subsectionState.activeFile.versionNumber,
      fileId: this.subsectionState.activeFile.id,
    };
    this.subsectionService.navigateSubsection(navigationParams);
  }

  public shareNewAsset(): void {
    if (this.treeType != TreeManagerTreeType.Subsection) {
      return;
    }

    const assetVm: ShareLinkAssetVM | undefined = {
      subsection: this.subsectionState.subsection,
      type: ShareLinkAssetEntityTypes.SUBSECTION,
    };

    if (this.subsectionState.activeTopic) {
      assetVm.topic = this.subsectionState.activeTopic;
      assetVm.type = ShareLinkAssetEntityTypes.FOLDER;
    }
    if (this.subsectionState.activeOption) {
      assetVm.option = this.subsectionState.activeOption;
      assetVm.type = ShareLinkAssetEntityTypes.SUBFOLDER;
    }
    if (this.subsectionState.activeVersion) {
      assetVm.version = this.subsectionState.activeVersion;
      assetVm.type = ShareLinkAssetEntityTypes.VERSION;
    }
    if (this.subsectionState.activeFile) {
      assetVm.file = this.subsectionState.activeFile;
      assetVm.type = ShareLinkAssetEntityTypes.FILE;
    }
    shareAsset({
      asset: assetVm,
      shareLinkService: this.shareLinkService,
      overlayService: this.overlayService,
      translatePipe: this.translatePipe,
      subsectionService: this.subsectionService,
    })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe();
  }
}
